import { fieldsFromSchema } from "./schema.ts";
import { propFeet, type ObjectState, type ObjectTypeDefinition } from "./objects.ts";
import type { ValidationIssue } from "./adapter.ts";
import type { JsonValue, MapDocument, PropBehaviour, PropInstance, TileRef } from "../document/types.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { CompileContext, TransitionStrip } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import { frameId } from "../assets/manifest.ts";

/**
 * Authoring a behaviour on a placed prop (ADR-053). The prop's own frame is the initial state's, so every
 * state past it needs a frame of its own before the world will compile; these pure helpers say which states
 * those are, what a freshly switched-on behaviour starts from, and what the editor should report about a map
 * so an author meets a refusal in the inspector rather than at publish, and is warned where a behaviour
 * compiles but blocks nothing or grows no default ring because its frame has no footprint (P1.1-T6).
 */

/**
 * The states a behaviour needs a frame for: every state past the initial one, in declaration order.
 *
 * @param type The object type.
 * @returns The states.
 */
export function furtherStates(type: ObjectTypeDefinition): readonly ObjectState[] {
  return type.states.filter((state) => state.id !== type.initial);
}

/**
 * The field values a behaviour starts with: the schema's defaults, exactly as placing an entity of a type
 * with the same schema starts.
 *
 * @param type The object type.
 * @returns The fields.
 */
export function behaviourFieldDefaults(type: ObjectTypeDefinition): { readonly [key: string]: JsonValue } {
  const fields: Record<string, JsonValue> = {};
  for (const field of fieldsFromSchema(type.schema)) {
    if (field.defaultValue !== null) {
      fields[field.name] = field.defaultValue;
    }
  }
  return fields;
}

/**
 * A prop's states and the frames they rest on, which are the frames its collision is stamped by (ADR-053
 * decision 3): the initial state on the prop's own frame, then each further state its behaviour has given a
 * frame, in declaration order (ADR-040 amendment of 2026-10-03, P1.6-T74). A further state with no frame yet is
 * left out, since nothing rests on it.
 *
 * @param prop The placed prop.
 * @param type The object type to read the states from.
 * @returns The states with their frames; empty when the prop carries no behaviour or a behaviour of another
 *   type.
 */
export function stateFrames(prop: PropInstance, type: ObjectTypeDefinition): readonly { readonly id: string; readonly frame: TileRef }[] {
  const behaviour = prop.behaviour;
  if (behaviour === undefined || behaviour.type !== type.type) {
    return [];
  }
  const further = furtherStates(type).flatMap((state) => {
    const frame = behaviour.frames?.[state.id];
    return frame === undefined ? [] : [{ id: state.id, frame }];
  });
  return [{ id: type.initial, frame: prop.frame }, ...further];
}

/**
 * The further states of a behaviour that have no frame yet.
 *
 * @param behaviour The behaviour.
 * @param type The object type it names.
 * @returns The state ids, in declaration order.
 */
export function missingStateFrames(behaviour: PropBehaviour, type: ObjectTypeDefinition): readonly string[] {
  return furtherStates(type)
    .filter((state) => behaviour.frames?.[state.id] === undefined)
    .map((state) => state.id);
}

/**
 * The animations of a behaviour that name no one-shot strip although a state-changing transition plays them
 * (ADR-058). The compiler reads an animation only through such a transition and refuses one whose frame
 * starts no strip, so a named animation that only a self-transition plays, or that no transition plays, is
 * not reported. Each name is reported once, in transition declaration order.
 *
 * @param behaviour The behaviour.
 * @param type The object type it names.
 * @param strips The one-shot strips the compiler plays, by the frame id of their first frame.
 * @returns The animation names and the frame id each names.
 */
function unplayableAnimations(behaviour: PropBehaviour, type: ObjectTypeDefinition, strips: Readonly<Record<string, TransitionStrip>>): readonly { readonly name: string; readonly start: string }[] {
  const out: { name: string; start: string }[] = [];
  for (const transition of type.transitions) {
    const name = transition.plays;
    const chosen = name === undefined ? undefined : behaviour.animations?.[name];
    if (name === undefined || chosen === undefined || transition.from === transition.to || out.some((o) => o.name === name)) {
      continue;
    }
    const start = frameId(chosen.tileset, null, chosen.tile);
    if (strips[start] === undefined) {
      out.push({ name, start });
    }
  }
  return out;
}

/**
 * Words joined as a list: `a`, `a and b`, `a, b and c`.
 *
 * @param words The words.
 * @param conjunction The word before the last, `and` or `or`.
 * @returns The list.
 */
function listed(words: readonly string[], conjunction: "and" | "or"): string {
  return words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} ${conjunction} ${words[words.length - 1] as string}`;
}

/**
 * What a behaviour on frames with no footprint does not do (P1.1-T6). A state the type blocks by its frame's
 * footprint (`auto`) blocks nothing where that frame has none, or only an empty one, which the game's
 * footprint policy leaves for flags, rugs, stairs, bridges, roofs and other walkable art; the compile accepts
 * it, so these are warnings, not refusals. The initial state is the one the author placed, and a thing that
 * blocks nothing where it stands (a gate that cannot be closed against anyone) is named; with no cells painted,
 * every such state also grows no default ring, since the ring grows around what the state blocks (ADR-053
 * decision 8). A further state with no frame yet is left to its own refusal, and a state declared `none` or
 * as cells is never named, since its frame's footprint is not what it blocks by. Where a `mustBlock` refusal
 * already says the prop blocks nothing, the blocking warning is left out and the activation warning stays.
 *
 * @param prop The placed prop.
 * @param behaviour Its behaviour.
 * @param type The object type the behaviour names.
 * @param footprints The footprints of the compile context by frame id, as the compiler reads them.
 * @param blocking Whether to give the blocking warning: false where a refusal says the same.
 * @returns The warnings' messages, the blocking one first.
 */
function footprintWarnings(prop: PropInstance, behaviour: PropBehaviour, type: ObjectTypeDefinition, footprints: Readonly<Record<string, PropFootprint>>, blocking: boolean): string[] {
  const bare = type.states
    .filter((state) => {
      const frame = state.id === type.initial ? prop.frame : behaviour.frames?.[state.id];
      const footprint = frame === undefined ? undefined : footprints[frameId(frame.tileset, null, frame.tile)];
      return state.footprint === "auto" && frame !== undefined && (footprint === undefined || footprint.runs.length === 0);
    })
    .map((state) => state.id);
  const out: string[] = [];
  if (blocking && bare.includes(type.initial)) {
    out.push(`prop ${prop.id} is a ${type.label} whose ${type.initial} frame has no footprint, so it blocks nothing while ${type.initial}; paint one in its Collision and drawing section`);
  }
  if (behaviour.activation === undefined && bare.length > 0) {
    out.push(`prop ${prop.id} is a ${type.label} whose ${listed(bare, "and")} ${bare.length === 1 ? "frame has" : "frames have"} no footprint, so it has no default cells to be used from while ${listed(bare, "or")}; paint cells in its Activation section`);
  }
  return out;
}

/**
 * Why a prop whose type declares `mustBlock` blocks nothing where it stands (ADR-058's amendment of 2026-10-04,
 * item 2), the first that applies: it is flipped vertically, which stamps nothing whatever its footprint; its
 * initial state declares no footprint or empty cells; or that state's footprint is derived from a frame whose
 * resolved footprint is absent or has no runs. The later states are not judged.
 *
 * @param prop The placed prop.
 * @param type Its type, declaring `mustBlock`.
 * @param footprints The footprints of the compile context by frame id, as the compiler reads them.
 * @returns The refusal's message in item 3's words, or null when the prop blocks where it stands.
 */
function mustBlockRefusal(prop: PropInstance, type: ObjectTypeDefinition, footprints: Readonly<Record<string, PropFootprint>>): string | null {
  const must = `a ${type.label} must block where it stands`;
  if (prop.flipY) {
    return `prop ${prop.id} is a ${type.label} flipped vertically, which blocks nothing, and ${must}; turn off its vertical flip`;
  }
  const declared = type.states.find((state) => state.id === type.initial)?.footprint ?? "auto";
  const frame = prop.frame;
  const blocks = declared === "auto" ? (footprints[frameId(frame.tileset, null, frame.tile)]?.runs.length ?? 0) > 0 : declared !== "none" && declared.cells.length > 0;
  return blocks ? null : `prop ${prop.id} is a ${type.label} that blocks nothing, and ${must}; paint a footprint for its ${type.initial} frame in its Collision and drawing section`;
}

/**
 * Where a behaviour prop stands when that is off the map. The compile lowers a behaviour prop to the bottom
 * centre of its own frame, sized as the compile sizes it (`frameSizeOf`, ADR-053 P1.1-T6 note 1), and
 * refuses the world when that point lies off the map; a thing on a wide frame placed against the right edge,
 * or any thing on the bottom row, stands there although its anchor is on the map. A prop driven by presence
 * (ADR-058 amendment, P1.3-T4) is never lowered and stands nowhere, so this is not asked of it.
 *
 * @param prop The placed prop.
 * @param map The map it is placed on.
 * @param context The compile context, for the frame's size.
 * @param tileSize Tile size in pixels.
 * @returns The point in tiles and the way back onto the map (`up` or `down`, `left` or `right`, or one of each
 *   joined by `and`), or null when the point is on the map.
 */
function offMap(prop: PropInstance, map: MapDocument, context: Pick<CompileContext, "collision" | "frameSizes">, tileSize: number): { readonly x: number; readonly y: number; readonly move: string } | null {
  const feet = propFeet(prop, frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile))?.w ?? 0, tileSize);
  const ways = [feet.y < 0 ? "down" : feet.y >= map.height ? "up" : null, feet.x < 0 ? "right" : feet.x >= map.width ? "left" : null].filter((way) => way !== null);
  return ways.length === 0 ? null : { x: feet.x, y: feet.y, move: ways.join(" and ") };
}

/**
 * Everything about a map's props the compiler would refuse: a prop placed outside the map, plain or not (the
 * compile refuses the world for it), a behaviour naming a type the game does not
 * register, a behaviour whose further state has no frame, an animation a state-changing transition plays
 * whose frame starts no one-shot strip, and a thing that would stand off the map, which a prop driven by
 * presence never is, since the compile does not lower it (ADR-058 amendment, P1.3-T4), and a prop of a type
 * declaring `mustBlock` that blocks nothing where it stands (ADR-058's amendment of 2026-10-04); then, marked
 * `warning`, what compiles but blocks nothing or grows no default ring because a frame has no footprint
 * (P1.1-T6). Reported per prop so the inspector can show the issues of the selected one.
 *
 * @param map The map.
 * @param types The registered object types.
 * @param context The compile context, read as the compiler reads it: its one-shot strips by the frame id of
 *   their first frame, its footprints and its frame sizes by frame id, each empty when the context holds none.
 * @param tileSize The world's tile size in pixels, for where a thing stands.
 * @returns The issues, in document order, each prop's refusals before its warnings.
 */
export function behaviourIssues(map: MapDocument, types: readonly ObjectTypeDefinition[], context: Pick<CompileContext, "collision" | "transitionStrips" | "frameSizes">, tileSize: number): readonly ValidationIssue[] {
  const strips = context.transitionStrips ?? {};
  const footprints = context.collision.footprints ?? {};
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const outside = prop.x < 0 || prop.y < 0 || prop.x >= map.width || prop.y >= map.height;
      if (outside) {
        issues.push({ mapId: map.id, kind: "prop.outside", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is placed at (${prop.x}, ${prop.y}), outside the map's ${map.width} by ${map.height} tiles; move it inside or it will not compile` });
      }
      const behaviour = prop.behaviour;
      if (behaviour === undefined) {
        continue;
      }
      const type = types.find((t) => t.type === behaviour.type);
      if (type === undefined) {
        issues.push({ mapId: map.id, kind: "behaviour.unregistered", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} carries behaviour ${behaviour.type}, which this game does not register` });
        continue;
      }
      for (const stateId of missingStateFrames(behaviour, type)) {
        issues.push({ mapId: map.id, kind: "behaviour.no-frame", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is a ${type.label} whose ${stateId} state has no frame; pick one or it will not compile` });
      }
      for (const { name, start } of unplayableAnimations(behaviour, type, strips)) {
        issues.push({ mapId: map.id, kind: "behaviour.no-strip", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is a ${type.label} whose ${name} animation names ${start}, which starts no one-shot strip; choose another or None` });
      }
      const off = type.presence === undefined && !outside ? offMap(prop, map, context, tileSize) : null;
      if (off !== null) {
        issues.push({ mapId: map.id, kind: "behaviour.off-map", layerId: layer.id, propId: prop.id, message: `prop ${prop.id} is a ${type.label} that stands at the bottom centre of its art, (${off.x}, ${off.y}), which is off the map; move it ${off.move} or it will not compile` });
      }
      const refusal = type.mustBlock === true ? mustBlockRefusal(prop, type, footprints) : null;
      if (refusal !== null) {
        issues.push({ mapId: map.id, kind: "behaviour.must-block", layerId: layer.id, propId: prop.id, message: refusal });
      }
      for (const message of footprintWarnings(prop, behaviour, type, footprints, refusal === null)) {
        issues.push({ mapId: map.id, kind: "behaviour.no-footprint", ignorable: true, layerId: layer.id, propId: prop.id, message, severity: "warning" });
      }
    }
  }
  return issues;
}
